import React from 'react';
import { styled, type SxProps, type Theme } from '@mui/material/styles';

/**
 * MUI v4's `<Grid>` layout, rebuilt on v9's styling engine so a screen moved off `@material-ui/core` keeps the
 * exact layout it was built with. v9's own Grid is not a drop-in: it spaces with gaps, lets nested containers
 * inherit spacing, and uses different breakpoints (md 900 / lg 1200 / xl 1536 against v4's 960 / 1280 / 1920).
 *
 * The rules and their order are v4's (`@material-ui/core/Grid`): container, item, alignment, spacing, then sizes
 * per breakpoint, later rules winning. Items carry v4's `MuiGrid-item` class and a container spaces
 * `> .MuiGrid-item`, so a LegacyGrid is spaced correctly inside a v4 Grid container and spaces v4 items inside it.
 *
 * For migrated screens only. New layout is Tailwind; replace this when a screen is redesigned.
 */

type GridSize = boolean | 'auto' | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
type Spacing = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;

export interface LegacyGridProps extends React.HTMLAttributes<HTMLElement> {
  container?: boolean;
  item?: boolean;
  spacing?: Spacing;
  xs?: GridSize;
  sm?: GridSize;
  md?: GridSize;
  lg?: GridSize;
  xl?: GridSize;
  direction?: 'row' | 'row-reverse' | 'column' | 'column-reverse';
  wrap?: 'nowrap' | 'wrap' | 'wrap-reverse';
  alignItems?: 'flex-start' | 'center' | 'flex-end' | 'stretch' | 'baseline';
  alignContent?: 'stretch' | 'center' | 'flex-start' | 'flex-end' | 'space-between' | 'space-around';
  justifyContent?: 'flex-start' | 'center' | 'flex-end' | 'space-between' | 'space-around' | 'space-evenly';
  zeroMinWidth?: boolean;
  component?: React.ElementType;
  sx?: SxProps<Theme>;
}

const BREAKPOINTS = { xs: 0, sm: 600, md: 960, lg: 1280, xl: 1920 } as const;

const sizeStyle = (size: Exclude<GridSize, false>) => {
  if (size === true) return { flexBasis: 0, flexGrow: 1, maxWidth: '100%' };
  if (size === 'auto') return { flexBasis: 'auto', flexGrow: 0, maxWidth: 'none' };
  // v4 keeps seven significant digits.
  const width = `${Math.round((size / 12) * 10e7) / 10e5}%`;
  return { flexBasis: width, flexGrow: 0, maxWidth: width };
};

const OWN_PROPS = new Set([
  'container',
  'item',
  'spacing',
  'xs',
  'sm',
  'md',
  'lg',
  'xl',
  'direction',
  'wrap',
  'alignItems',
  'alignContent',
  'justifyContent',
  'zeroMinWidth'
]);

const GridRoot = styled('div', { shouldForwardProp: (prop) => !OWN_PROPS.has(String(prop)) })<LegacyGridProps>(
  (props) => {
    const s: Record<string, unknown> = {};
    if (props.container)
      Object.assign(s, { boxSizing: 'border-box', display: 'flex', flexWrap: 'wrap', width: '100%' });
    if (props.item) Object.assign(s, { boxSizing: 'border-box', margin: 0 });
    if (props.zeroMinWidth) s.minWidth = 0;
    if (props.direction && props.direction !== 'row') s.flexDirection = props.direction;
    if (props.wrap && props.wrap !== 'wrap') s.flexWrap = props.wrap;
    if (props.alignItems && props.alignItems !== 'stretch') s.alignItems = props.alignItems;
    if (props.alignContent && props.alignContent !== 'stretch') s.alignContent = props.alignContent;
    if (props.justifyContent && props.justifyContent !== 'flex-start') s.justifyContent = props.justifyContent;
    if (props.container && props.spacing) {
      const gap = props.spacing * 8;
      // An item that is also a spaced container carries v4's global .MuiGrid-item class, whose `margin: 0` (from
      // v4's own stylesheet, wherever a v4 Grid is mounted) would tie with this margin; one more class settles it.
      if (props.item) s['&.MuiGrid-item'] = { margin: -gap / 2 };
      else s.margin = -gap / 2;
      Object.assign(s, { width: `calc(100% + ${gap}px)`, '& > .MuiGrid-item': { padding: gap / 2 } });
    }
    for (const key of Object.keys(BREAKPOINTS) as (keyof typeof BREAKPOINTS)[]) {
      const size = props[key];
      if (size === undefined || size === false) continue;
      if (key === 'xs') Object.assign(s, sizeStyle(size));
      else s[`@media (min-width:${BREAKPOINTS[key]}px)`] = sizeStyle(size);
    }
    // One class, like any MUI component, so `sx` overrides these the way a makeStyles class overrode v4's Grid.
    return s;
  }
);

const LegacyGrid = React.forwardRef<HTMLElement, LegacyGridProps>(
  ({ className, item, component, sx, ...rest }, ref) => {
    // An item that is also a spaced container sets its margin one class higher (above); an `sx` margin on it, like a
    // makeStyles class did, still has to win - so an object `sx` there is raised to match. (The cast: nesting an
    // `sx` object under a selector is valid at runtime, but SxProps' union cannot express it.)
    const raised = item && rest.container && rest.spacing && sx && typeof sx === 'object' && !Array.isArray(sx);
    return (
      <GridRoot
        ref={ref as React.Ref<HTMLDivElement>}
        as={component}
        item={item}
        className={[item ? 'MuiGrid-item' : '', className].filter(Boolean).join(' ') || undefined}
        sx={raised ? ({ '&&': sx } as SxProps<Theme>) : sx}
        {...rest}
      />
    );
  }
);
LegacyGrid.displayName = 'LegacyGrid';

export default LegacyGrid;
